import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { StoreResponses, usersApi } from '@/stores/api';
import { MegacardRequestDto, UserDto, UserRegistrationDto, UserSubmitDto } from '@/api';
import { StoreResponse } from '@/stores/response';
import { app } from '@/main';
import { Config } from '@/util/Config';
import router from '@/router';
import Routes from '@/util/Routes';
import ErrorKey from '@/util/ErrorKey';
import LocalStorageKeys from '@/util/LocalStorageKeys';

@Module({
  namespaced: true,
  name: 'UserStore',
})
export default class UserStore extends VuexModule {
  private user: UserDto | null = null;
  private editUser: UserSubmitDto | null = null;
  private pageSize: number = 50;
  private language: string = Config.DEFAULT_LANGUAGE;
  private languageSet: boolean = false;
  private initFinished: Promise<StoreResponse<UserDto>> | null = null;

  public get currentLanguage(): string {
    return this.language;
  }

  public get currentUser(): UserDto | null {
    return this.user;
  }

  public get currentPageSize(): number {
    return this.pageSize;
  }

  public get currentEditUser(): UserSubmitDto | null {
    return this.editUser;
  }

  public get hasMegacard(): boolean {
    return !!this.user?.megacard;
  }

  public get megacardInfo(): MegacardRequestDto | null {
    return this.user?.megacard ?? null;
  }

  public get languageExplicitlySet(): boolean {
    return this.languageSet;
  }

  @Mutation
  public setUserPageSize(pageSize: number) {
    this.pageSize = pageSize;
  }

  @Mutation
  public copyUserForEditing() {
    if (this.user) {
      this.editUser = {
        salutation: this.user.salutation, firstname: this.user.firstname,
        lastname: this.user.lastname, email: this.user.email,
        birthDate: this.user.birthDate, company: this.user.company,
      };
    }
  }

  @Action
  public async fetchUserOnce(): Promise<StoreResponse<UserDto>> {
    if (this.initFinished === null) {
      this.context.commit('setInitFinishedPromise', this.fetchUser());
    }
    return this.initFinished!!;
  }

  @Action
  public async changeUserLanguage(language: string): Promise<StoreResponse<Response>> {
    try {
      const response = await usersApi.updateUserLanguage(language);
      this.context.commit('setLocalLanguage', language);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.LanguageChangeAction);
    }
  }

  @Action
  public async changeUserDefaultBillingAddress(addressId: number): Promise<StoreResponse<Response>> {
    try {
      const response = await usersApi.updateDefaultBillingAddress(addressId);
      this.fetchUser();
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.UpdateDefaultAddress);
    }
  }

  @Action
  public async changeUserDefaultDeliveryAddress(addressId: number): Promise<StoreResponse<Response>> {
    try {
      const response = await usersApi.updateDefaultDeliveryAddress(addressId);
      this.fetchUser();
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.UpdateDefaultAddress);
    }
  }

  @Action
  public applyLanguageFromLocalStorage() {
    const storedLanguage = localStorage.getItem(LocalStorageKeys.SelectedLanguage);
    if (storedLanguage) {
      this.context.commit('setLocalLanguage', storedLanguage);
    }
  }

  @Action
  public async savePageSize(): Promise<StoreResponse<number>> {
    try {
      await usersApi.updateUserPageSize(this.pageSize);
      this.context.commit('setUserPageSize', this.pageSize);
      return StoreResponses.success(this.pageSize);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.UserPageSizeSaveAction);
    }
  }

  @Mutation
  private setInitFinishedPromise(promise: Promise<StoreResponse<UserDto>>) {
    this.initFinished = promise;
  }

  @Action
  public async fetchUser(): Promise<StoreResponse<UserDto>> {
    try {
      const response = await usersApi.getCurrentUser();
      this.context.commit('setCurrentUser', response.data);
      if (response.data.language) {
        this.context.commit('setLocalLanguage', response.data.language);
      }
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      if (e.errorKey && e.errorKey === ErrorKey.NoSuchUserException && router.currentRoute.name !== Routes.Register) {
        await router.push({
          name: Routes.Register,
          query: {fromCart: (router.currentRoute.name === Routes.ShoppingCart).toString()},
        });
      }
      return StoreResponses.error(e, ErrorKey.UserFetchAction);
    }
  }

  @Action
  public async createUser(registration: UserRegistrationDto): Promise<StoreResponse<Response>> {
    try {
      const response = await usersApi.createUser(registration);
      this.context.commit('setCurrentUser', registration.model);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.UserCreateAction);
    }
  }

  @Action
  public async updateCurrentUser(newInfo: UserSubmitDto): Promise<StoreResponse<UserDto>> {
    try {
      const response = await usersApi.updateUserData(newInfo);
      this.context.commit('setCurrentUser', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.UserUpdateAction);
    }
  }

  @Mutation
  public setLocalLanguage(language: string): void {
    this.language = language;
    if (app) {
      app.$i18n.locale = language;
    }
    localStorage.setItem(LocalStorageKeys.SelectedLanguage, language);
    this.languageSet = true;
  }

  @Mutation
  public markLanguageExplicitlySet(): void {
    this.languageSet = true;
  }

  @Mutation
  private setCurrentUser(currentUser: UserDto) {
    this.user = currentUser;
  }
}
